/**
 * SistemaSection Component - System customization (logos) section
 */
import React from 'react';
import { Upload, Image, Trash2 } from 'lucide-react';
import PermissionGate from '../PermissionGate';

interface SistemaSectionProps {
  // Main logo
  logoPreview: string | null;
  currentLogo: string | null;
  isUploadingLogo: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onLogoFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDeleteLogo: () => Promise<void>;

  // Mini logo
  miniLogoPreview: string | null;
  currentMiniLogo: string | null;
  isUploadingMiniLogo: boolean;
  miniLogoFileInputRef: React.RefObject<HTMLInputElement>;
  onMiniLogoFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDeleteMiniLogo: () => Promise<void>;
}

export const SistemaSection: React.FC<SistemaSectionProps> = ({
  logoPreview,
  currentLogo,
  isUploadingLogo,
  fileInputRef,
  onLogoFileChange,
  onDeleteLogo,
  miniLogoPreview,
  currentMiniLogo,
  isUploadingMiniLogo,
  miniLogoFileInputRef,
  onMiniLogoFileChange,
  onDeleteMiniLogo,
}) => {
  return (
    <div className="space-y-6">
      {/* Seção de Personalização do Sistema */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 p-6">
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Logo do Sistema</label>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Upload de logo personalizado. Formatos aceitos: PNG, JPG, SVG (máx. 5MB)
            </p>

            {/* Preview do logo atual */}
            <div className="flex items-start gap-6">
              <div className="flex-shrink-0">
                <div className="w-40 h-40 border-2 border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center bg-gray-50 dark:bg-gray-900 overflow-hidden">
                  {logoPreview ? (
                    <img
                      src={logoPreview}
                      alt="Preview do logo"
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : currentLogo ? (
                    <img
                      src={currentLogo}
                      alt="Logo atual"
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <div className="text-center p-4">
                      <Image className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                      <p className="text-xs text-gray-500">Nenhum logo</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex-1 space-y-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                  onChange={onLogoFileChange}
                  className="hidden"
                />

                <PermissionGate tela="CONFIGURACOES_VIEW" acao="editar">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploadingLogo}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
                  >
                    {isUploadingLogo ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Fazer Upload
                      </>
                    )}
                  </button>
                </PermissionGate>

                {currentLogo && (
                  <PermissionGate tela="CONFIGURACOES_VIEW" acao="editar">
                    <button
                      onClick={onDeleteLogo}
                      disabled={isUploadingLogo}
                      className="bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Remover Logo
                    </button>
                  </PermissionGate>
                )}

                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  O logo será exibido em todas as telas do sistema (Web, Mobile, Flutter).
                </p>
              </div>
            </div>
          </div>

          {/* Seção de Mini Logo para Sidebar Colapsado */}
          <div className="mt-8 pt-8 border-t-2 border-gray-200 dark:border-gray-700">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Mini Logo (Sidebar Colapsado)
            </label>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Logo pequeno quadrado para sidebar colapsado. Recomendado: 40x40px (máx. 1MB)
            </p>

            {/* Preview do mini logo */}
            <div className="flex items-start gap-6">
              <div className="flex-shrink-0">
                <div className="w-24 h-24 border-2 border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center bg-gray-50 dark:bg-gray-900 overflow-hidden">
                  {miniLogoPreview ? (
                    <img
                      src={miniLogoPreview}
                      alt="Preview do mini logo"
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : currentMiniLogo ? (
                    <img
                      src={currentMiniLogo}
                      alt="Mini logo atual"
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <div className="text-center p-4">
                      <Image className="w-8 h-8 mx-auto text-gray-400 mb-1" />
                      <p className="text-xs text-gray-500">Sem mini logo</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex-1 space-y-3">
                <input
                  ref={miniLogoFileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                  onChange={onMiniLogoFileChange}
                  className="hidden"
                />

                <PermissionGate tela="CONFIGURACOES_VIEW" acao="editar">
                  <button
                    onClick={() => miniLogoFileInputRef.current?.click()}
                    disabled={isUploadingMiniLogo}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
                  >
                    {isUploadingMiniLogo ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Upload Mini Logo
                      </>
                    )}
                  </button>
                </PermissionGate>

                {currentMiniLogo && (
                  <PermissionGate tela="CONFIGURACOES_VIEW" acao="editar">
                    <button
                      onClick={onDeleteMiniLogo}
                      disabled={isUploadingMiniLogo}
                      className="bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Remover Mini Logo
                    </button>
                  </PermissionGate>
                )}

                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Este logo aparece quando o menu lateral está colapsado (modo compacto).
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SistemaSection;
